import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCamera, FaFolderOpen } from 'react-icons/fa';
import { loadCrewEntries, loadCrews } from '../api/crewsApi';
import { uploadReceiptRedemption } from '../api/redemptionsApi';
import { prepareImageUpload } from '../utils/photoUpload';
import {
  beginReceiptSubmission,
  classifyReceiptRedemption,
  endReceiptSubmission,
  getReceiptRedemptionMessage,
  getReceiptRedeemedAmount,
} from '../utils/receiptRedemptionStatus';
import '../styles/receipt-redemptions.css';

function formatCurrency(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fileLabel(file) {
  if (!file) {
    return 'Noch keine Bondatei ausgewaehlt';
  }

  return file.name || 'Bondatei bereit';
}

function normalizeReceiptMode(mode) {
  return mode === 'create_redeemed_entry' ? 'create_redeemed_entry' : 'match_existing';
}

function requestReceiptPosition() {
  return new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null
        });
      },
      () => resolve(null),
      {
        enableHighAccuracy: false,
        timeout: 2500,
        maximumAge: 60000
      }
    );
  });
}

export default function ReceiptRedemptionModal({
  isOpen,
  expectedAmount = 0,
  selectedEntryIds = [],
  initialMode = 'match_existing',
  title = 'Pfandbon hochladen',
  onClose,
  onSuccess,
}) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState(() => normalizeReceiptMode(initialMode));
  const [scope, setScope] = useState('personal');
  const [crewId, setCrewId] = useState('');
  const [crews, setCrews] = useState([]);
  const [crewOpenAmount, setCrewOpenAmount] = useState(null);
  const [crewAmountLoading, setCrewAmountLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const submissionLock = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setError('');
    setResult(null);
    setMode(normalizeReceiptMode(initialMode));
    setScope('personal');
    setCrewId('');
    setCrewOpenAmount(null);
    setCrewAmountLoading(false);
    submissionLock.current = false;
    loadCrews()
      .then(data => setCrews(Array.isArray(data)
        ? data.filter(item => item?.is_member || item?.is_owner || item?.can_view_private_data)
        : []))
      .catch(() => setCrews([]));
  }, [initialMode, isOpen]);

  useEffect(() => {
    if (!isOpen || scope !== 'crew' || !crewId) {
      setCrewOpenAmount(null);
      setCrewAmountLoading(false);
      return undefined;
    }

    let cancelled = false;
    setCrewAmountLoading(true);

    loadCrewEntries(crewId)
      .then(entries => {
        if (cancelled) return;

        const openAmount = (Array.isArray(entries) ? entries : []).reduce((sum, entry) => {
          const status = String(entry?.status || 'open');
          const receiptId = Number(entry?.redeemed_receipt_id || 0);
          if (status === 'redeemed' || receiptId > 0) {
            return sum;
          }
          return sum + Number(entry?.amount || 0);
        }, 0);

        setCrewOpenAmount(Math.max(0, Math.round(openAmount * 100) / 100));
      })
      .catch(() => {
        if (!cancelled) {
          setCrewOpenAmount(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCrewAmountLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [crewId, isOpen, scope]);

  const crewOptions = useMemo(() => {
    return crews.map(crew => ({
      id: Number(crew.id || 0),
      title: crew?.title?.rendered || crew.title || `Crew #${crew.id}`
    })).filter(crew => crew.id > 0);
  }, [crews]);

  const canSubmit = !!file && !loading;
  const matchedAmount = getReceiptRedeemedAmount(result);
  const reviewMessage = result?.match_summary?.review_message || '';
  const trustScore = Number(result?.match_summary?.trust_score || 0);
  const resultClassification = classifyReceiptRedemption(result);
  const successLike = resultClassification === 'success';
  const isCreateMode = mode === 'create_redeemed_entry';
  const displayExpectedAmount = !isCreateMode && scope === 'crew' && crewOpenAmount !== null
    ? crewOpenAmount
    : expectedAmount;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!file || loading || !beginReceiptSubmission(submissionLock)) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const [preparedFile, position] = await Promise.all([
        file?.type === 'application/pdf'
          ? Promise.resolve(file)
          : prepareImageUpload(file, {
            outputBaseName: 'pfandbon'
          }),
        requestReceiptPosition()
      ]);
      const payload = await uploadReceiptRedemption(preparedFile, {
        mode,
        scope: isCreateMode ? 'personal' : scope,
        crewId: !isCreateMode && scope === 'crew' ? crewId : null,
        selectedEntryIds: isCreateMode ? [] : selectedEntryIds,
        position,
        onProgress: progress => setResult(progress),
      });
      setResult(payload);
      try {
        Promise.resolve(onSuccess?.(payload)).catch(err => {
          console.warn('[Pfandpirat] Bonstatus ist gespeichert; optionale App-Aktualisierung fehlgeschlagen.', err);
        });
      } catch (callbackError) {
        console.warn('[Pfandpirat] Bonstatus ist gespeichert; optionale App-Aktualisierung fehlgeschlagen.', callbackError);
      }
    } catch {
      setError('Der Pfandbon konnte aufgrund eines technischen Fehlers nicht vollständig verarbeitet werden. Bitte versuche es später erneut.');
    } finally {
      endReceiptSubmission(submissionLock);
      setLoading(false);
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="pfp-receipt-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="pfp-receipt-modal__backdrop" onClick={() => !loading && onClose?.()} />
      <section className="pfp-receipt-modal__panel">
        <header className="pfp-receipt-modal__header">
          <div>
            <span>Pfand mit Nachweis bestätigen</span>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={() => !loading && onClose?.()} aria-label="Schließen">
            x
          </button>
        </header>

        <form className="pfp-receipt-modal__body" onSubmit={handleSubmit}>
          <div className="pfp-receipt-modal__capture-warning">
            <strong>Vor dem Einlösen fotografieren</strong>
            <span>
              Manche Märkte behalten den Pfandbon an der Kasse ein. Bitte fotografiere ihn vorher, sonst können Punkte später nicht sicher gutgeschrieben werden.
            </span>
          </div>

          <div className="pfp-receipt-modal__mode">
            <label>
              <input
                type="radio"
                name="receipt-mode"
                value="match_existing"
                checked={mode === 'match_existing'}
                onChange={() => setMode('match_existing')}
              />
              Offene Einträge bestätigen
            </label>
            <label>
              <input
                type="radio"
                name="receipt-mode"
                value="create_redeemed_entry"
                checked={mode === 'create_redeemed_entry'}
                onChange={() => {
                  setMode('create_redeemed_entry');
                  setScope('personal');
                }}
              />
              Bon als neuen Eintrag erfassen
            </label>
          </div>

          <div className="pfp-receipt-modal__summary">
            <span>{isCreateMode ? 'Bonbetrag' : (scope === 'crew' ? 'Erwarteter Crew-Betrag' : 'Erwarteter Betrag')}</span>
            <strong>{isCreateMode ? 'wird erkannt' : (crewAmountLoading ? '...' : formatCurrency(displayExpectedAmount))}</strong>
            {isCreateMode ? <em>Es wird ein anonymer Summen-Eintrag erzeugt.</em> : null}
            {!isCreateMode && scope === 'crew' ? <em>Dein offener Betrag: {formatCurrency(expectedAmount)}</em> : null}
            {selectedEntryIds.length ? <em>{selectedEntryIds.length} ausgewählte Einträge</em> : null}
          </div>

          {!isCreateMode && crewOptions.length > 0 ? (
            <div className="pfp-receipt-modal__scope">
              <label>
                <input
                  type="radio"
                  name="receipt-scope"
                  value="personal"
                  checked={scope === 'personal'}
                  onChange={() => setScope('personal')}
                />
                Nur meine offenen Einträge
              </label>
              <label>
                <input
                  type="radio"
                  name="receipt-scope"
                  value="crew"
                  checked={scope === 'crew'}
                  onChange={() => {
                    setScope('crew');
                    setCrewId(prev => prev || String(crewOptions[0]?.id || ''));
                  }}
                />
                Meine Crew mitprüfen
              </label>
              {scope === 'crew' ? (
                <select value={crewId} onChange={event => setCrewId(event.target.value)}>
                  {crewOptions.map(crew => (
                    <option key={crew.id} value={crew.id}>{crew.title}</option>
                  ))}
                </select>
              ) : null}
            </div>
          ) : null}

          <div className="pfp-receipt-modal__capture">
            <label className="pfp-receipt-modal__camera">
              <input
                type="file"
                accept="image/*"
                onChange={event => setFile(event.target.files?.[0] || null)}
              />
              <FaCamera aria-hidden="true" />
              <span>Pfandbon fotografieren</span>
            </label>

            <div className={`pfp-receipt-modal__file-state ${file ? 'has-file' : ''}`}>
              {fileLabel(file)}
            </div>

            <label className="pfp-receipt-modal__fallback">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf,.pdf,image/*"
                onChange={event => setFile(event.target.files?.[0] || null)}
              />
              <FaFolderOpen aria-hidden="true" />
              <span>Foto oder E-Bon nachtragen</span>
            </label>

            <small>JPG, PNG, WebP oder PDF. Der Bon wird als Nachweis gespeichert.</small>
          </div>

          {error ? <div className="pfp-receipt-modal__error">{error}</div> : null}

          {loading && !result ? (
            <div className="pfp-receipt-modal__result" role="status">
              <strong>Dein Pfandbon wird geprüft …</strong>
            </div>
          ) : null}

          {result ? (
            <div className={`pfp-receipt-modal__result ${successLike ? 'is-success' : 'is-warning'}`}>
              <strong>{getReceiptRedemptionMessage(result)}</strong>
              {reviewMessage && resultClassification !== 'success' ? <small>{reviewMessage}</small> : null}
              {trustScore > 0 ? <small>Vertrauensscore: {trustScore}/100</small> : null}
              <span>
                Erkannt: {formatCurrency(result.deposit_amount)} - Zugeordnet: {formatCurrency(matchedAmount)}
              </span>
              {result.merchant ? <small>{result.merchant} - {result.receipt_date || 'Datum offen'}</small> : null}
              {result.created_entry_id ? <small>Eintrag #{result.created_entry_id}</small> : null}
            </div>
          ) : null}

          <footer className="pfp-receipt-modal__actions">
            <button type="button" onClick={() => !loading && onClose?.()} disabled={loading}>
              Schließen
            </button>
            <button type="submit" disabled={!canSubmit}>
              {loading ? 'Bon wird geprüft ...' : 'Bon prüfen und Pfand bestätigen'}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
